import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';

interface PainPoint {
  title: string;
  description: string;
}

interface ProblemSectionProps {
  headline: string;
  intro?: string;
  painPoints: PainPoint[];
  transition: string;
  fascinationBullet?: string;
}

// ============================================
// Curtain Wipe Reveal Component
// ============================================

const CurtainReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <div ref={ref} className="relative overflow-hidden">
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.5, delay }}
      >
        {children}
      </motion.div>

      {/* Curtain wipe overlay */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-amber-500/20 via-orange-500/30 to-red-500/20"
        initial={{ x: 0 }}
        animate={isInView ? { x: '100%' } : {}}
        transition={{ duration: 0.8, delay, ease: [0.645, 0.045, 0.355, 1] }}
      />
    </div>
  );
};

// ============================================
// Warning Icon with Shake
// ============================================

const WarningIcon: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  return (
    <motion.div
      animate={isActive ? { rotate: [0, -5, 5, -5, 5, 0] } : {}}
      transition={{ duration: 0.5, repeat: isActive ? Infinity : 0, repeatDelay: 3 }}
    >
      <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    </motion.div>
  );
};

// ============================================
// X to Checkmark Animation
// ============================================

const XToCheckmark: React.FC<{ isTransformed: boolean }> = ({ isTransformed }) => {
  return (
    <div className="relative w-8 h-8">
      <AnimatePresence mode="wait">
        {!isTransformed ? (
          <motion.div
            key="x"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5, rotate: 90 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </motion.div>
        ) : (
          <motion.div
            key="check"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <motion.svg
              className="w-6 h-6 text-emerald-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <motion.path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M5 13l4 4L19 7"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </motion.svg>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// Marker Underline Text Effect
// ============================================

const MarkerHighlight: React.FC<{ children: React.ReactNode; color?: string }> = ({
  children,
  color = 'from-amber-500/30 to-orange-500/30'
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  return (
    <span ref={ref} className="relative inline">
      <span className="relative z-10">{children}</span>
      <motion.span
        className={`absolute bottom-0 left-0 right-0 h-[40%] bg-gradient-to-r ${color} -z-0`}
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 0.6, delay: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
        style={{ transformOrigin: 'left' }}
      />
    </span>
  );
};

// ============================================
// Money Burned Counter
// ============================================

const MoneyBurnedCounter: React.FC = () => {
  const [amount, setAmount] = useState(847000);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: false, margin: '-100px' });
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (isInView && !isActive) {
      setIsActive(true);
    }
  }, [isInView, isActive]);

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      // $70K per month = ~$2.3K per day = ~$97 per hour = ~$1.62 per minute
      setAmount((prev) => prev + Math.floor(Math.random() * 50 + 75));
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive]);

  const formatMoney = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div ref={ref} className="relative">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        className="bg-gradient-to-br from-red-500/10 via-red-500/5 to-transparent rounded-2xl border border-red-500/20 p-6 backdrop-blur-sm"
      >
        <div className="flex items-center gap-3 mb-3">
          <motion.div
            animate={isActive ? { scale: [1, 1.2, 1] } : {}}
            transition={{ duration: 1, repeat: Infinity }}
            className="w-3 h-3 rounded-full bg-red-500"
          />
          <span className="text-red-400 text-sm font-medium uppercase tracking-wider">Live Cost Calculator</span>
        </div>

        <div className="text-center py-4">
          <p className="text-gray-400 text-sm mb-2">Money burned while you're "still evaluating":</p>
          <motion.p
            key={amount}
            initial={{ scale: 1.05 }}
            animate={{ scale: 1 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-red-400"
          >
            {formatMoney(amount)}
          </motion.p>
          <motion.p
            className="text-red-400/60 text-sm mt-2"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            +$97/hour in lost productivity
          </motion.p>
        </div>

        {/* Animated flames */}
        <div className="absolute bottom-0 left-0 right-0 h-16 overflow-hidden rounded-b-2xl pointer-events-none">
          <motion.div
            className="absolute bottom-0 left-1/4 w-8 h-8 bg-gradient-to-t from-red-500/40 to-orange-500/0 rounded-full blur-md"
            animate={{ y: [0, -20, 0], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute bottom-0 left-1/2 w-10 h-10 bg-gradient-to-t from-orange-500/40 to-yellow-500/0 rounded-full blur-md"
            animate={{ y: [0, -25, 0], opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
          />
          <motion.div
            className="absolute bottom-0 right-1/4 w-8 h-8 bg-gradient-to-t from-red-500/40 to-orange-500/0 rounded-full blur-md"
            animate={{ y: [0, -18, 0], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
          />
        </div>
      </motion.div>
    </div>
  );
};

// ============================================
// Split Screen Comparison
// ============================================

const SplitScreenComparison: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const theirWay = [
    '6+ months to first deployment',
    'Endless strategy meetings',
    '$400K+ consulting fees',
    'No working software',
    'Team frustration',
  ];

  const ourWay = [
    '23 days average deployment',
    'Action-oriented sprints',
    'Clear, fixed pricing',
    'Production-ready AI',
    'Team empowerment',
  ];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
      className="relative grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-white/10"
    >
      {/* Their Way */}
      <div className="relative bg-gradient-to-br from-red-500/10 to-red-500/5 p-5 sm:p-6 md:p-8">
        <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl" />

        <div className="relative">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h4 className="text-red-400 font-semibold">The Traditional Way</h4>
          </div>

          <ul className="space-y-4">
            {theirWay.map((item, index) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="flex items-center gap-3 text-gray-400"
              >
                <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
                {item}
              </motion.li>
            ))}
          </ul>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div className="w-12 h-12 rounded-full bg-zinc-900 border border-white/20 flex items-center justify-center">
          <span className="text-white font-bold text-sm">VS</span>
        </div>
      </div>

      {/* Our Way */}
      <div className="relative bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 p-5 sm:p-6 md:p-8">
        <div className="absolute top-0 left-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl" />

        <div className="relative">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h4 className="text-emerald-400 font-semibold">Our Approach</h4>
          </div>

          <ul className="space-y-4">
            {ourWay.map((item, index) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, x: 20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="flex items-center gap-3 text-gray-300"
              >
                <svg className="w-5 h-5 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {item}
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// Workflow Efficiency Diagram
// ============================================

const WorkflowDiagram: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [showEfficient, setShowEfficient] = useState(false);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setShowEfficient(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [isInView]);

  const inefficientSteps = [
    { icon: '📥', label: 'Manual Input', time: '2hrs' },
    { icon: '👀', label: 'Review', time: '4hrs' },
    { icon: '📝', label: 'Process', time: '6hrs' },
    { icon: '✅', label: 'Approve', time: '2hrs' },
    { icon: '📤', label: 'Output', time: '1hr' },
  ];

  const efficientSteps = [
    { icon: '🤖', label: 'AI Input', time: '5min' },
    { icon: '⚡', label: 'Auto-Process', time: '30sec' },
    { icon: '📤', label: 'Output', time: '1min' },
  ];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
      className="bg-white/[0.02] backdrop-blur-sm rounded-2xl border border-white/10 p-8 overflow-hidden"
    >
      <h4 className="text-white font-semibold mb-8 text-center">Workflow Transformation</h4>

      <div className="space-y-8">
        {/* Inefficient workflow */}
        <div className={`transition-opacity duration-500 ${showEfficient ? 'opacity-40' : 'opacity-100'}`}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-red-400 text-sm font-medium">Current: 15+ hours</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            {inefficientSteps.map((step, index) => (
              <React.Fragment key={step.label}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: index * 0.15 }}
                  className="flex flex-col items-center gap-2"
                >
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-xl">
                    {step.icon}
                  </div>
                  <span className="text-gray-500 text-xs">{step.label}</span>
                  <span className="text-red-400 text-xs font-mono">{step.time}</span>
                </motion.div>
                {index < inefficientSteps.length - 1 && (
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={isInView ? { scaleX: 1 } : {}}
                    transition={{ delay: index * 0.15 + 0.1 }}
                    className="flex-1 h-px bg-gradient-to-r from-red-500/50 to-red-500/20"
                    style={{ transformOrigin: 'left' }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Arrow transition */}
        <div className="flex justify-center">
          <motion.div
            animate={showEfficient ? { y: [0, 5, 0] } : {}}
            transition={{ duration: 1, repeat: Infinity }}
            className="text-emerald-400"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </motion.div>
        </div>

        {/* Efficient workflow */}
        <AnimatePresence>
          {showEfficient && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <motion.div
                  className="w-3 h-3 rounded-full bg-emerald-500"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
                <span className="text-emerald-400 text-sm font-medium">With AI: ~6 minutes</span>
                <span className="text-emerald-400/60 text-xs ml-2">(99.3% faster)</span>
              </div>
              <div className="flex items-center justify-center gap-4">
                {efficientSteps.map((step, index) => (
                  <React.Fragment key={step.label}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.15 }}
                      className="flex flex-col items-center gap-2"
                    >
                      <motion.div
                        className="w-14 h-14 rounded-xl bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center text-2xl"
                        animate={{ boxShadow: ['0 0 0 rgba(16, 185, 129, 0)', '0 0 20px rgba(16, 185, 129, 0.3)', '0 0 0 rgba(16, 185, 129, 0)'] }}
                        transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                      >
                        {step.icon}
                      </motion.div>
                      <span className="text-gray-300 text-xs">{step.label}</span>
                      <span className="text-emerald-400 text-xs font-mono">{step.time}</span>
                    </motion.div>
                    {index < efficientSteps.length - 1 && (
                      <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: index * 0.15 + 0.1 }}
                        className="w-16 h-1 bg-gradient-to-r from-emerald-500 to-emerald-500/50 rounded-full"
                        style={{ transformOrigin: 'left' }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ============================================
// Expandable Accordion
// ============================================

interface AccordionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const Accordion: React.FC<AccordionProps> = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-white/[0.02]">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <span className="text-white font-medium">{title}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-gray-400"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
          >
            <div className="px-5 pb-5 text-gray-400 border-t border-white/5 pt-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// Enhanced Pain Point Card
// ============================================

const PainPointCard: React.FC<{
  point: PainPoint;
  index: number;
}> = ({ point, index }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [isHovering, setIsHovering] = useState(false);
  const [isTransformed, setIsTransformed] = useState(false);
  const [showAccordion, setShowAccordion] = useState(false);
  const isInView = useInView(cardRef, { once: true, margin: '-50px' });

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });

  const transformProgress = useTransform(scrollYProgress, [0.3, 0.7], [0, 1]);

  useEffect(() => {
    const unsubscribe = transformProgress.on('change', (v) => {
      if (v > 0.5 && !isTransformed) {
        setIsTransformed(true);
      }
    });
    return () => unsubscribe();
  }, [transformProgress, isTransformed]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  }, []);

  const rotateX = isHovering ? (mousePosition.y - 50) * -0.05 : 0;
  const rotateY = isHovering ? (mousePosition.x - 50) * 0.05 : 0;

  const cardVariants = {
    hidden: {
      opacity: 0,
      x: -40,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        delay: index * 0.12,
        ease: [0.215, 0.61, 0.355, 1] as const,
      },
    },
  };

  return (
    <CurtainReveal delay={index * 0.1}>
      <motion.div
        ref={cardRef}
        className="group relative rounded-2xl overflow-hidden"
        style={{
          transformStyle: 'preserve-3d',
          perspective: 1000,
        }}
        variants={cardVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        whileHover={{
          rotateX,
          rotateY,
          z: 15,
          scale: 1.01,
          x: 8,
          transition: { duration: 0.2, ease: 'easeOut' },
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          setIsHovering(false);
          setMousePosition({ x: 50, y: 50 });
        }}
      >
        {/* Animated border - red to green transition */}
        <motion.div
          className="absolute -inset-[1px] rounded-2xl opacity-100 transition-all duration-700"
          animate={{
            background: isTransformed
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.5), rgba(52, 211, 153, 0.5))'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.5), rgba(249, 115, 22, 0.5))',
          }}
          style={{
            backgroundSize: '200% 200%',
          }}
        />

        {/* Glass morphism background */}
        <motion.div
          className="absolute inset-[1px] rounded-2xl backdrop-blur-xl transition-colors duration-700"
          animate={{
            backgroundColor: isTransformed ? 'rgba(16, 185, 129, 0.05)' : 'rgba(24, 24, 27, 0.9)',
          }}
        />

        {/* Cursor-following highlight */}
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background: `radial-gradient(400px circle at ${mousePosition.x}% ${mousePosition.y}%, ${
              isTransformed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(251, 191, 36, 0.1)'
            }, transparent 40%)`,
          }}
        />

        {/* Card content */}
        <div className="relative z-10 p-6 sm:p-8">
          <div className="flex gap-6">
            {/* Icon container with X to Checkmark */}
            <div className="flex-shrink-0">
              <motion.div
                className="relative w-12 h-12 rounded-xl flex items-center justify-center transition-colors duration-700"
                style={{
                  backgroundColor: isTransformed ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.1)',
                  borderColor: isTransformed ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                  borderWidth: 1,
                }}
              >
                <XToCheckmark isTransformed={isTransformed} />
              </motion.div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 mb-2">
                <h3 className="text-lg sm:text-xl font-semibold text-white transition-colors duration-300">
                  {point.title}
                </h3>
                {!isTransformed && <WarningIcon isActive={isInView} />}
              </div>
              <p className="text-zinc-400 leading-relaxed mb-4">
                {point.description}
              </p>

              {/* Learn more accordion */}
              <button
                onClick={() => setShowAccordion(!showAccordion)}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors"
              >
                <span>Learn more</span>
                <motion.svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  animate={{ rotate: showAccordion ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </motion.svg>
              </button>

              <AnimatePresence>
                {showAccordion && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 text-sm text-gray-500 border-t border-white/5 mt-4">
                      <p>
                        Companies lose an average of <MarkerHighlight>$70,000+ per month</MarkerHighlight> in productivity
                        due to inefficient manual processes. Our AI solutions have helped clients reduce this waste by
                        up to 73% within the first 90 days of deployment.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Transformation indicator */}
        <AnimatePresence>
          {isTransformed && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute top-4 right-4 px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full"
            >
              <span className="text-emerald-400 text-xs font-medium">Solution Available</span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </CurtainReveal>
  );
};

// ============================================
// Main Problem Section
// ============================================

export const ProblemSection: React.FC<ProblemSectionProps> = ({
  headline,
  intro,
  painPoints,
  transition,
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-24 md:py-32 bg-[#0a0a0a] overflow-hidden">
      {/* Animated background blob */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-500/5 rounded-full blur-[150px]"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Top border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-amber-400"
              animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">The Problem</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight"
          >
            {headline}
          </motion.h2>

          {intro && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
              className="text-lg text-zinc-400 leading-relaxed max-w-3xl mx-auto"
            >
              {intro}
            </motion.p>
          )}
        </div>

        {/* Money Burned Counter */}
        <div className="mb-12">
          <MoneyBurnedCounter />
        </div>

        {/* Pain points */}
        <div className="space-y-4 mb-16" style={{ perspective: 1000 }}>
          {painPoints.map((point, index) => (
            <PainPointCard key={index} point={point} index={index} />
          ))}
        </div>

        {/* Split Screen Comparison */}
        <div className="mb-16">
          <SplitScreenComparison />
        </div>

        {/* Workflow Diagram */}
        <div className="mb-16">
          <WorkflowDiagram />
        </div>

        {/* FAQ Accordions */}
        <div className="space-y-3 mb-16">
          <h3 className="text-white font-semibold mb-4">Common Questions</h3>
          <Accordion title="How quickly can we see ROI?">
            Most clients see measurable results within 30-60 days. Our fastest deployment was
            11 days from kickoff to production. The average ROI in the first year is 340% based
            on documented client savings.
          </Accordion>
          <Accordion title="What if we've tried AI before and it failed?">
            73% of AI initiatives fail because of implementation, not technology. We've built
            127+ production systems. Our approach focuses on quick wins that build momentum
            and prove value before expanding scope.
          </Accordion>
          <Accordion title="Do we need a large AI/ML team?">
            No. We handle the heavy lifting. Your team stays focused on your core business
            while we build, deploy, and maintain the AI systems. We provide full documentation
            and training so you're never dependent on us.
          </Accordion>
        </div>

        {/* Transition */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="text-center"
        >
          <p className="text-xl sm:text-2xl text-zinc-300 font-medium max-w-2xl mx-auto">
            {transition}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
